import { ComponentRef, Injectable, ViewContainerRef, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {environment} from '@environments/environment';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {Settings} from './settings.interface';
import {map, switchMap, toArray} from 'rxjs/operators';
import {isTool, skipModalError} from '@app/shared/_models/auth';
import {
  notificationWebhookDeliveryEvents,
  notificationWebhookEngagementEvents,
  settingsUbsubLanguages
} from './settings.model';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private http = inject(HttpClient);


  readonly apiUrl = environment.apiUrl;
  readonly newApiUrl = environment.newApiUrl;
  private newPrefix = `${this.newApiUrl}/tools`;

  private readonly userPrefix = `${this.newApiUrl}/user`;

  private _tools = new BehaviorSubject<Settings.Tool[]>([]);
  readonly tools = this._tools.asObservable();

  private _bccs = new BehaviorSubject<Settings.BccResponse>({active_id: null, items:[]});
  readonly bccs = this._bccs.asObservable();

  private _alerts = new BehaviorSubject<Settings.AlertItem[]>([]);
  readonly alerts = this._alerts.asObservable();

  private _templates = new BehaviorSubject<Settings.TemplateItem[]>([]);
  readonly templates = this._templates.asObservable();

  private _footerInfo = new BehaviorSubject<Settings.TemplateResponse>({active_id: null, items:[]});
  readonly footerInfo = this._footerInfo.asObservable();

  private _templateInfo = new BehaviorSubject<Settings.TemplateResponse>({active_id: null, items:[]});
  readonly templateInfo = this._templateInfo.asObservable();

  private _unsubscribeLinks = new BehaviorSubject<Settings.UnsubItem[]>([]);
  readonly unsubscribeLinks = this._unsubscribeLinks.asObservable();

  getTools() {
    return this.http.get<Settings.Tool[]>(`${this.newPrefix}`)
      .subscribe(tools => this._tools.next(tools));
  }

  getBCCs() {
    return forkJoin([
      this.http.get<Settings.Active>(`${this.newPrefix}/bcc/active`),
      this.http.get<Settings.BccItem[]>(`${this.newPrefix}/bcc`),
    ]).pipe(
      map<[Settings.Active, Settings.BccItem[]], Settings.BccResponse>(([{active_id}, items]) => ({
        active_id,
        items
      }))
    ).subscribe((res) => {
      this._bccs.next(res);
    });
  }

  addBcc(email: string, name: string) {
    return this.http.post<Settings.BccItem>(
      `${this.newPrefix}/bcc`, {email, name}
    );
  }

  updateBcc(bcc: Settings.RestUpdateBccRequest) {
    return this.http.put<Settings.BccItem>(
      `${this.newPrefix}/bcc/${bcc.id}`, {email: bcc.email, name: bcc.name}
    );
  }

  verifyBCC(token: string) {
    return this.http.post<{ email: string }>(`${this.newPrefix}/bcc/verify/${token}`, {}, {
      context: skipModalError(['all'])
    });
  }

  enableTool(toolId: string, enable: boolean) {
    return this.http.put<Settings.Tool>(
      `${this.newPrefix}/${toolId}/${enable ? 'enable' : 'disable'}`, {}, {
        context: isTool(toolId)
      }
    );
  }

  getGoogleAnalytics() {
    return this.http.get<Settings.GoogleAnalyticsSettings>(
      `${this.newPrefix}/google_analytics`
    );
  }

  updateGoogleAnalyticsSettings(settings: Settings.GoogleAnalyticsSettings) {
    return this.http.put<Settings.GoogleAnalyticsSettings>(
      `${this.newPrefix}/google_analytics`, settings
    );
  }

  getGlobals() {
    return this.http.get<Settings.Global>(
      `${this.newApiUrl}/settings/global`
    );
  }

  updateUserDelimiter(delimiter: Settings.Delimiter) {
    return this.http.put<Settings.Delimiter>(
      `${this.userPrefix}/delimiter`, {delimiter}, {
        context: isTool('global')
      }
    );
  }

  updateGlobals(global: Settings.Global) {
    return this.http.put<Settings.Global>(
      `${this.newApiUrl}/settings/global`, global, {
        context: isTool('global')
      }
    );
  }

  deleteBCC(id: number) {
    return this.http.delete<Settings.BccItem>(
      `${this.newPrefix}/bcc/${id}`
    );
  }

  updateWebhook(data: Settings.RestInsertWebhookRequest) {
    return this.http.put<Settings.Webhook>(`${this.newPrefix}/webhooks`, data);
  }

  private getWebhook() {
    return this.http.get<Settings.Webhook>(
      `${this.newPrefix}/webhooks`
    );
  }

  openSidebarSettings(
    viewContainerRef: ViewContainerRef,
    component: any
  ){
    const componentRef: ComponentRef<any> = viewContainerRef.createComponent(
      component
    );

    componentRef.instance.visible.set(true);
    componentRef.instance.isModal = true;

    componentRef.instance.visible.subscribe((visible) => {
      if (!visible) {
        // Destroy synchronously while the drawer's internal `_visible` is still
        // true (the [(visible)] input is only re-evaluated on the next CD tick).
        // This lets PrimeNG 21's Drawer.ngOnDestroy() run destroyModal() and
        // remove its body-appended overlay mask. A deferred destroy (setTimeout)
        // instead tears down the mask's animationend listener before it fires,
        // orphaning the mask (p-drawer-mask ...-leave-active) and locking the UI.
        componentRef.destroy();
      }
    });

  }

  getWebhookData() {

    return this.getWebhook().pipe(
      map<Settings.Webhook, Settings.WebhookData>(settings => {
        if (settings.is_pro) {
          return {
            status: 'success',
            ...settings,
            events: {
              delivery_events: settings.events.filter(ev => ~notificationWebhookDeliveryEvents.indexOf(ev.event)),
              engagement_events: settings.events.filter(ev => ~notificationWebhookEngagementEvents.indexOf(ev.event))
            }
          };
        }
        return {
          status: 'error',
          ...settings,
          events: {
            delivery_events: [],
            engagement_events: []
          }
        };
      })
    );
  }

  getPeriodicReports() {
    return this.http.get<Settings.PeriodicReportItem[]>(`${this.newPrefix}/periodic_reports`);
  }

  addPeriodicReport(day_number: number, frequency: Settings.PeriodicReportFrequency, hour: number) {
    return this.http.post<Settings.PeriodicReportItem>(`${this.newPrefix}/periodic_reports`, {
      day_number,
      frequency,
      hour
    });
  }

  updatePeriodicReport(id: number, day_number: number, frequency: Settings.PeriodicReportFrequency, hour: number) {
    return this.http.put<Settings.PeriodicReportItem>(`${this.newPrefix}/periodic_reports/${id}`, {
      day_number,
      frequency,
      hour
    });
  }

  deletePeriodicReport(id: number) {
    return this.http.delete<Settings.PeriodicReportItem>(`${this.newPrefix}/periodic_reports/${id}`);
  }

  updateActivityReports(activityRequests: Settings.RestUpdateActivityData){
    let operations: Observable<any>[] = [];

    Object.keys(activityRequests).forEach((frequency: Settings.PeriodicReportFrequency) => {
      const activityRequest = activityRequests[frequency],
        {id, enable, day_number, hour} = activityRequest;
      let operation: Settings.PeriodicReportOp = 'DELETE';

      if (enable) {
        if (id !== 0) {
          operation = 'UPDATE';
        } else {
          operation = 'INSERT';
        }
      } else {
        if (id === 0) {
          operation = 'DUNNO';
        }
      }

      switch (operation) {
        case 'DELETE':
          operations = [...operations, this.deletePeriodicReport(id)];
          break;
        case 'INSERT':
          operations = [...operations, this.addPeriodicReport(day_number, frequency, hour)];
          break;
        case 'UPDATE':
          operations = [...operations, this.updatePeriodicReport(id, day_number, frequency, hour)];
          break;
      }
    });

    if (operations.length === 0)
      return of(null);
    return forkJoin(operations);
  }

  getAlerts() {
    return this.http.get<{ count: number, results: Settings.AlertItem[] }>(`${this.newApiUrl}/tools/alerts`)
      .subscribe(alerts => this._alerts.next(alerts?.results));
  }

  addAlert(email: string, percentage: number) {
    return this.http.post<Settings.AlertItem>(
      `${this.newApiUrl}/tools/alerts`, {email, percentage}
    );
  }

  updateAlert(id: number, email: string, percentage: number) {
    return this.http.patch<Settings.AlertItem>(
      `${this.newApiUrl}/tools/alerts/${id}`, {email, percentage}
    );
  }

  deleteAlert(id: number) {
    return this.http.delete<Settings.AlertItem>(
      `${this.newApiUrl}/tools/alerts/${id}`
    );
  }

  addCustomTrackingDomain(domain: string, subdomain: string) {
    return this.http.post<{ id: number }>(
      `${this.newApiUrl}/tools/link_branding`, {
        domain,
        ...subdomain && subdomain != '' && {
          subdomain: subdomain+'.'+domain
        }
      }
    ).pipe(
      map(({id}) => ({
        id: +id
      }))
    );
  }

  getCustomTrackingDomain(id:number) {
    return this.http.get<Settings.CustomTrackingDomain>(
      `${this.newApiUrl}/tools/link_branding/${id}`,{}
    );
  }

  isLastUsedCustomTrackingDomain(id:number, active: boolean = false) {
    if (active){
      return of({is_last_used: false});
    }
    return this.http.get<{ is_last_used: boolean }>(
      `${this.newApiUrl}/tools/link_branding/is_last_used/${id}`,{}
    );
  }

  verifyCustomTrackingDomain(id:number) {
    return this.http.post<Settings.CustomTrackingVerificationResponse>(
      `${this.newApiUrl}/tools/link_branding/verify/${id}`,{}
    ).pipe(
      map(res => {
        if (res.message !== 'success'){
          throw new HttpErrorResponse({error: res.message, status: 404});
        }
        return res;
      })
    );
  }

  assignCustomTrackingDomainAsDefault(id:number, value: boolean) {
    return this.http.put<Settings.CustomTrackingDomain>(
      `${this.newApiUrl}/tools/link_branding/default/${id}/${value ? 'enable': 'disable'}`,{}
    );
  }

  enableCustomTrackingMatchingSender(enabled: boolean){
    return this.http.put<Settings.CustomTrackingDomain>(
      `${this.newApiUrl}/tools/link_branding_match_sender/${enabled ? 'enable': 'disable'}`,{}
    );
  }

  enableCustomTrackingDomain(id:number, value: boolean) {
    return this.http.put<Settings.CustomTrackingDomain>(
      `${this.newApiUrl}/tools/link_branding/enabled/${id}/${value ? 'enable': 'disable'}`,{}
    );
  }

  deleteCustomTrackingDomain(id: number) {
    return this.http.delete<Settings.RestDeleteCustomTrackingDomainResponse>(
      `${this.newApiUrl}/tools/link_branding/${id}`
    );
  }

  getDomainsInfo(){
    return this.http.get<{ all_domains_disabled: boolean, no_default_domain: boolean, match_sender:boolean }>(`${this.newApiUrl}/tools/link_branding/domains_info`);
  }

  getCustomTrackingDomains(
    params?: Partial<Settings.TrackingDomainRequest>
  ){
    return this.http.get<Settings.TrackingDomainData>(`${this.newApiUrl}/tools/link_branding`, {
      params
    });
  }

  getTemplates(type : Settings.TemplateType = 'templates') {
    return forkJoin([
      this.http.get<Settings.Active>(`${this.newPrefix}/${type}/active`),
      this.http.get<Settings.TemplateItem[]>(`${this.newPrefix}/${type}`)
    ]).pipe(
      map<[Settings.Active, Settings.TemplateItem[]], Settings.TemplateResponse>(([{active_id}, items]) => ({
        active_id,
        items
      }))
    ).subscribe((res) => {
      this[type === 'templates' ? '_templateInfo' : '_footerInfo'].next(res);
    });
  }

  setDefaultTemplate(templateId: number, type : Settings.TemplateType = 'templates') {
    return this.http.put<Settings.Tool>(
      `${this.newPrefix}/${type}/active/${templateId}`, {}
    );
  }

  addTemplate(data: Settings.TemplateBody, type : Settings.TemplateType = 'templates') {
    return this.http.post<Settings.TemplateItem>(
      `${this.newPrefix}/${type}`, data
    );
  }

  updateTemplate(template: Settings.RestUpdateTemplateRequest, type : Settings.TemplateType = 'templates') {
    const {id, ...footerBody} = template;
    return this.http.put<Settings.TemplateItem>(
      `${this.newPrefix}/${type}/${id}`, footerBody
    );
  }

  deleteTemplate(id: number, type : Settings.TemplateType = 'templates') {
    return this.http.delete<Settings.TemplateItem>(
      `${this.newPrefix}/${type}/${id}`
    );
  }

  getFooters() {
    return this.getTemplates('footers');
  }

  setDefaultFooter(footerId: number) {
    return this.setDefaultTemplate(footerId, 'footers');
  }

  addFooter(template: Settings.TemplateBody) {
    return this.addTemplate(template, 'footers');
  }

  updateFooter(template: Settings.RestUpdateTemplateRequest) {
    return this.updateTemplate(template, 'footers');
  }

  deleteFooter(id: number) {
    return this.deleteTemplate(id, 'footers');
  }

  getUnsubscribeLinks() {
    return this.http.get<Settings.UnsubItemPart[]>(`${this.newPrefix}/unsubscribe_links`)
      .pipe(
        switchMap(unsubscribeLinks => unsubscribeLinks),
        map<Settings.UnsubItemPart, Settings.UnsubItem>(unsub => {
          return {
            ...unsub,
            placeholder: `<% unsub_${unsub.language} %>`,
            fullLanguage: settingsUbsubLanguages.find(
              language => language.id === unsub.language
            )?.key
          };
        }),
        toArray()
      )
      .subscribe(unsubscribeLinks => this._unsubscribeLinks.next(unsubscribeLinks));
  }

  deleteUnsubscribeLink(language: string) {
    return this.http.delete<Settings.UnsubItemPart>(
      `${this.newPrefix}/unsubscribe_links/${language}`
    );
  }

  patchUnsubscribeLink(language: string, text: string) {
    return this.http.put<Settings.UnsubItemPart>(
      `${this.newPrefix}/unsubscribe_links`, {language, text}
    );
  }

  html2text(html: string): string {
    let data = html;
    // remove BR tags and replace them with line break
    data = data.replace(/<br>/gi, '\n');
    data = data.replace(/<br\s\/>/gi, '\n');
    data = data.replace(/<br\/>/gi, '\n');

    // add two line endings to H1, H2, H3, H4, H5, H6, DIV, P, PRE
    data = data.replace(/(<\/(h[1-6]|div|p|pre)>)/ig, '$1\n\n');

    // remove tags
    data = data.replace(/<[^a\/][^>]*>/g, '');
    data = data.replace(/<\/+[^a][^>]*>/g, '');
    data = data.replace(/<[^>]+>\s*<\/[^>]+>/ig, '');

    // replace links
    data = data.replace(/<a[^>]+href="([^"]+)"[^>]*>([^<]+)<\/a>/ig, '[$2]<$1>');
    data = data.replace(/((\[)[\s\t]+|[\s\t]+(\]))/g, '$2$3');

    // right trimming
    data = data.replace(/^([^\s\t]+)[\s\t]*$/gm, '$1');

    // remove more than two \n\r?
    data = data.replace(/(\n\r?\s*\t*){2,}/gm, '\n\n');

    data = data.replace(/&nbsp;/gi, ' ');
    data = data.replace(/&amp;/gi, '&');
    data = data.replace(/&quot;/gi, '"');
    data = data.replace(/&lt;/gi, '<');
    data = data.replace(/&gt;/gi, '>');
    return data;
  }

  setDefaultEmail(id: number) {
    return this.http.put<Settings.Tool>(
      `${this.newPrefix}/bcc/active/${id}`, {}
    );
  }
}
